'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, RotateCcw, Trophy } from 'lucide-react';

interface Props { onBack: () => void; }

type Cell = 'X' | 'O' | null;
type Board = Cell[];

interface LeaderboardEntry { name: string; wins: number; losses: number; draws: number; score: number; }

const WINNING_LINES = [
  [0,1,2],[3,4,5],[6,7,8],
  [0,3,6],[1,4,7],[2,5,8],
  [0,4,8],[2,4,6],
];

function checkWinner(board: Board): { winner: Cell; line: number[] } | null {
  for (const line of WINNING_LINES) {
    const [a,b,c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line };
    }
  }
  return null;
}

const INITIAL_LEADERBOARD: LeaderboardEntry[] = [
  { name: 'Arjun K.', wins: 24, losses: 8, draws: 4, score: 2800 },
  { name: 'Priya S.', wins: 19, losses: 11, draws: 6, score: 2200 },
  { name: 'Marcus T.', wins: 15, losses: 14, draws: 3, score: 1750 },
  { name: 'Yuki H.', wins: 12, losses: 10, draws: 8, score: 1600 },
  { name: 'Sofia R.', wins: 10, losses: 15, draws: 5, score: 1250 },
];

export default function TicTacToeGame({ onBack }: Props) {
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [isXTurn, setIsXTurn] = useState(true);
  const [winResult, setWinResult] = useState<{ winner: Cell; line: number[] } | null>(null);
  const [isDraw, setIsDraw] = useState(false);
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 });
  const [totalMoves, setTotalMoves] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard] = useState<LeaderboardEntry[]>(INITIAL_LEADERBOARD);
  const [gameXP, setGameXP] = useState(0);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const handleClick = useCallback((idx: number) => {
    if (board[idx] || winResult || isDraw) return;
    const newBoard = [...board];
    newBoard[idx] = isXTurn ? 'X' : 'O';
    const result = checkWinner(newBoard);
    const moves = totalMoves + 1;
    setBoard(newBoard);
    setTotalMoves(moves);
    if (result) {
      setWinResult(result);
      const xpGained = result.winner === 'X' ? 150 : 50;
      setGameXP(prev => prev + xpGained);
      setScores(prev => ({ ...prev, [result.winner!]: prev[result.winner! as 'X' | 'O'] + 1 }));
      setLastResult(result.winner === 'X' ? '🏆 Player X Wins! +150 XP' : '🏆 Player O Wins! +50 XP');
    } else if (moves === 9) {
      setIsDraw(true);
      setScores(prev => ({ ...prev, draws: prev.draws + 1 }));
      setGameXP(prev => prev + 30);
      setLastResult('🤝 Draw! +30 XP');
    } else {
      setIsXTurn(!isXTurn);
    }
  }, [board, isXTurn, winResult, isDraw, totalMoves]);

  const resetGame = useCallback(() => {
    setBoard(Array(9).fill(null));
    setIsXTurn(true);
    setWinResult(null);
    setIsDraw(false);
    setTotalMoves(0);
    setLastResult(null);
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="btn-ghost p-2 rounded-lg">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">⭕ Tic Tac Toe</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Classic X vs O — get three in a row</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowLeaderboard(!showLeaderboard)}
            className="btn-ghost flex items-center gap-2 text-sm">
            <Trophy size={16} /> Leaderboard
          </button>
          <button onClick={resetGame} className="btn-ghost flex items-center gap-2 text-sm">
            <RotateCcw size={16} /> New Game
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Game area */}
        <div className="lg:col-span-2">
          {/* Score cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Player X', value: scores.X, color: '#7C3AED', active: isXTurn && !winResult && !isDraw },
              { label: 'Draws', value: scores.draws, color: '#64748B', active: false },
              { label: 'Player O', value: scores.O, color: '#06B6D4', active: !isXTurn && !winResult && !isDraw },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-4 text-center transition-all duration-200"
                style={{
                  background: s.active ? `${s.color}22` : 'var(--card)',
                  border: `2px solid ${s.active ? s.color : 'var(--border)'}`,
                }}>
                <p className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
                {s.active && <div className="w-2 h-2 rounded-full mx-auto mt-2 animate-pulse" style={{ background: s.color }} />}
              </div>
            ))}
          </div>

          {/* Status */}
          <div className="rounded-xl p-3 mb-6 text-center"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {winResult ? (
              <p className="font-bold text-lg" style={{ color: winResult.winner === 'X' ? '#7C3AED' : '#06B6D4' }}>
                🎉 Player {winResult.winner} wins!
              </p>
            ) : isDraw ? (
              <p className="font-bold text-lg text-foreground">🤝 It&apos;s a draw!</p>
            ) : (
              <p className="font-semibold text-foreground">
                <span style={{ color: isXTurn ? '#7C3AED' : '#06B6D4' }}>Player {isXTurn ? 'X' : 'O'}</span>
                &apos;s turn
              </p>
            )}
          </div>

          {/* Board */}
          <div className="flex justify-center mb-6">
            <div className="grid grid-cols-3 gap-3" style={{ width: 'min(360px, 100%)' }}>
              {board.map((cell, idx) => {
                const isWinCell = winResult?.line.includes(idx);
                return (
                  <button
                    key={idx}
                    onClick={() => handleClick(idx)}
                    disabled={!!cell || !!winResult || isDraw}
                    className="aspect-square rounded-2xl flex items-center justify-center text-4xl font-bold transition-all duration-150"
                    style={{
                      background: isWinCell ? (cell === 'X' ? 'rgba(124,58,237,0.3)' : 'rgba(6,182,212,0.3)') : 'var(--card)',
                      border: `2px solid ${isWinCell ? (cell === 'X' ? '#7C3AED' : '#06B6D4') : 'var(--border)'}`,
                      color: cell === 'X' ? '#7C3AED' : '#06B6D4',
                      cursor: cell || winResult || isDraw ? 'default' : 'pointer',
                      transform: isWinCell ? 'scale(1.05)' : 'scale(1)',
                    }}
                  >
                    {cell}
                  </button>
                );
              })}
            </div>
          </div>

          {/* XP & result */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl p-4 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <p className="text-2xl font-bold text-gradient-primary">{gameXP}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>XP Earned This Session</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <p className="text-2xl font-bold text-foreground">{scores.X + scores.O + scores.draws}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Games Played</p>
            </div>
          </div>

          {lastResult && (
            <div className="mt-3 rounded-xl p-3 text-center font-semibold"
              style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#A78BFA' }}>
              {lastResult}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <Trophy size={16} style={{ color: '#F59E0B' }} /> Arcade Leaderboard
          </h3>
          <div className="space-y-3">
            {leaderboard.map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: i === 0 ? 'rgba(245,158,11,0.1)' : 'var(--muted)', border: `1px solid ${i === 0 ? 'rgba(245,158,11,0.3)' : 'transparent'}` }}>
                <span className="text-lg font-bold w-6 text-center"
                  style={{ color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#CD7F32' : 'var(--muted-foreground)' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{entry.name}</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {entry.wins}W · {entry.losses}L · {entry.draws}D
                  </p>
                </div>
                <span className="text-sm font-bold" style={{ color: '#7C3AED' }}>{entry.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-xl text-center" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Your Session Score</p>
            <p className="text-xl font-bold" style={{ color: '#7C3AED' }}>{gameXP}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
